// src/utils/logger.js
// Système de logging sécurisé
// Les logs sont désactivés en production pour éviter les fuites d'information

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Logger sécurisé
 * - Logs normaux uniquement en développement
 * - Erreurs toujours loggées (pour le monitoring)
 * - Performance tracking en développement uniquement
 */
export const logger = {
  /**
   * Log standard (uniquement en développement)
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Log d'erreur (toujours actif pour le monitoring)
   */
  error: (...args) => {
    console.error(...args);
    // TODO: Envoyer à un service de monitoring (Sentry, etc.)
  },

  /**
   * Log d'avertissement (uniquement en développement)
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Log d'information (uniquement en développement)
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Log de debug (uniquement en développement)
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  }
};

/**
 * Mesure de performance
 */
export const performance = {
  /**
   * Démarre un timer
   */
  start: (label) => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * Termine un timer
   */
  end: (label) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
};

/**
 * Log sécurisé d'erreur Firebase
 * Nettoie les informations sensibles avant de logger
 */
export const logFirebaseError = (error, context = '') => {
  // Créer une version nettoyée de l'erreur
  const cleanError = {
    code: error.code,
    message: error.message,
    context: context
  };

  // Ne pas logger les détails sensibles
  const sensitiveFields = ['credential', 'token', 'password', 'email'];
  
  if (error.customData) {
    cleanError.customData = {};
    Object.keys(error.customData).forEach(key => {
      if (!sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        cleanError.customData[key] = error.customData[key];
      }
    });
  }

  logger.error('🔥 Erreur Firebase:', cleanError);
  
  // En production, envoyer à un service de monitoring
  if (process.env.NODE_ENV === 'production') {
    // TODO: Envoyer à Sentry ou autre service
  }
};

/**
 * Log sécurisé de succès (uniquement en développement)
 */
export const logSuccess = (message, data = null) => {
  if (isDevelopment) {
    logger.log('✅', message, data || '');
  }
};

/**
 * Log sécurisé d'avertissement (uniquement en développement)
 */
export const logWarning = (message, data = null) => {
  if (isDevelopment) {
    logger.warn('⚠️', message, data || '');
  }
};

/**
 * Log sécurisé d'information (uniquement en développement)
 */
export const logInfo = (message, data = null) => {
  if (isDevelopment) {
    logger.info('ℹ️', message, data || '');
  }
};

export default logger;
